import { CreditCard, Paperclip, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtMoney } from '../../shared/format'
import { usePayments } from '../api'
import { useRequestVoid } from '../voids'
import { useFinanceLookups } from '../ui'
import { Money, Section } from './FinanceBits'

/** Tahsilat ve ödeme hareketleri; silinen ödeme kaydın kalan tutarını geri açar. */
export function PaymentsList({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation()
  const payments = usePayments()
  const voids = useRequestVoid()
  const { tx, partyName, accountName, accountDetail } = useFinanceLookups()
  const { pageRows, pager } = usePagination(payments.data ?? [])

  const onDelete = (id: string, amount: string) => voids.request('payment', id, t('finance.pay.deleteBody', { amount: fmtMoney(amount) }))

  return (
    <Section title={t('finance.pay.movements')} icon={CreditCard} bodyClassName="p-0">
      {payments.isError ? (
        <ErrorState error={payments.error} onRetry={() => void payments.refetch()} />
      ) : payments.isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : !payments.data?.length ? (
        <EmptyState icon={CreditCard} title={t('finance.pay.empty')} />
      ) : (
        <>
          <ul className="divide-y">
            {pageRows.map((p) => {
              const x = tx.get(p.transactionId)
              const income = x?.kind === 'income'
              return (
                <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
                  <div className="min-w-0 flex-1 basis-56">
                    <p className="truncate font-medium">{x ? partyName(x.counterpartyId) : '—'}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {fmtDate(p.date)} · {accountName(p.accountId)}
                      {accountDetail(p.accountId) ? ` (${accountDetail(p.accountId)})` : ''} · {t(`finance.channel.${p.channel}`)}
                      {p.reference ? ` · ${p.reference}` : ''}
                    </p>
                  </div>
                  {p.hasReceipt ? (
                    <Badge tone="success" className="rounded-md">
                      <Paperclip className="size-3" aria-hidden />
                      {t('finance.pay.receiptOk')}
                    </Badge>
                  ) : (
                    <Badge tone="warning" className="rounded-md">{t('finance.pay.receiptMissing')}</Badge>
                  )}
                  <span className={income ? 'font-semibold text-success' : 'font-semibold text-destructive'}>
                    {income ? '+' : '−'}
                    <Money value={p.amount} />
                  </span>
                  {canWrite ? (
                    <Button size="icon" variant="ghost" className="size-8 hover:text-destructive" aria-label={t('common.delete')} onClick={() => void onDelete(p.id, p.amount)}>
                      <Trash2 />
                    </Button>
                  ) : null}
                </li>
              )
            })}
          </ul>
          <Pager {...pager} />
        </>
      )}
    </Section>
  )
}
